import { useEffect, useState } from 'react';
import { leaderId as findLeader, percentOf, votersFor } from '../lib/format';

export function captionsFromItems(items, count) {
  const captions = Array(Math.max(count || 0, items?.length || 0)).fill('');
  (items || []).forEach((item, index) => {
    const position = Number.isInteger(item?.position) ? item.position : index;
    captions[position] = item?.description || '';
  });
  return captions;
}

// Grid shape per option count. layout 'grid' (frame B feed): 1 → one square,
// 2 → two across, 3 → three across, 4 → 2×2, every tile square.
// layout 'fill' (the booth, which should fill the screen): 2 → two 3:4
// portraits side by side, 3 → two squares over one 2:1 banner, 4 → 2×2.
function gridShape(n, layout) {
  if (n === 4) return { cols: 2, aspect: 'aspect-square' };
  if (n === 3) return layout === 'fill' ? { cols: 2, aspect: 'aspect-square' } : { cols: 3, aspect: 'aspect-[3/1]' };
  if (n === 2) return { cols: 2, aspect: layout === 'fill' ? 'aspect-[3/2]' : 'aspect-[2/1]' };
  return { cols: 1, aspect: 'aspect-square' };
}

// Width ÷ height of one tile.
function tileRatio(n, layout, index) {
  if (layout !== 'fill') return 1;
  if (n === 2) return 3 / 4;
  if (n === 3 && index === 2) return 2;
  return 1;
}

// Each sprite cell is a square photo. A square tile shows it directly; a
// non-square tile shows it cover-cropped (centred) through an inner square
// layer, so the photo is never stretched.
function coverSquare(ratio) {
  if (ratio < 1) {
    const w = 1 / ratio;
    return { width: `${w * 100}%`, height: '100%', left: `${(-(w - 1) / 2) * 100}%`, top: 0 };
  }
  return { width: '100%', height: `${ratio * 100}%`, top: `${(-(ratio - 1) / 2) * 100}%`, left: 0 };
}

function useSpriteStatus(src) {
  const [status, setStatus] = useState(src ? 'loading' : 'failed');
  useEffect(() => {
    if (!src) {
      setStatus('failed');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    const image = new Image();
    image.onload = () => { if (!cancelled) setStatus('loaded'); };
    image.onerror = () => { if (!cancelled) setStatus('failed'); };
    image.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return status;
}

/**
 * The poll sprite as per-option photo tiles. The sprite is N photos side by
 * side, so each tile shows it at N×100% width, shifted to index/(N-1).
 *
 * variant 'edge'    — the feed card (frame B): no rounding, 2 px gutters.
 * variant 'rounded' — the share / poll / booth pages: rounded corners.
 *
 * With `onVote` (and not yet `revealed`) each tile is a button. Once revealed,
 * every tile shows its percentage badge, a coral bar, the names that picked it
 * (from voters_by_item, else the item's own voters), the winner in green and
 * "your vote" on the viewer's choice.
 */
export default function PollTiles({
  src,
  items = null,
  count,
  alt = 'Poll image',
  variant = 'edge',
  onVote = null,
  revealed = false,
  votedItemId = null,
  totalVotes = 0,
  voting = false,
  votersByItem = null,
  showLabels = true,
  layout = 'grid',
}) {
  const status = useSpriteStatus(src);
  const n = Math.max(1, Math.min(Number(count || items?.length) || 1, 4));
  const { cols, aspect } = gridShape(n, layout);
  const rounded = variant === 'rounded';
  const votable = typeof onVote === 'function' && !revealed;
  const captions = captionsFromItems(items, n);
  // Items ordered by position so tile i shows option i.
  const ordered = Array.from({ length: n }).map((_, i) =>
    (items || []).find((it, idx) => (Number.isInteger(it?.position) ? it.position : idx) === i) || null
  );
  const winner = revealed ? findLeader(ordered) : null;

  // No photo (or it failed to load): with real options the tiles still render
  // on the surface colour so the poll stays votable; with none, a placeholder.
  const hasOptions = ordered.some((it) => it?.id != null);
  if ((!src || status === 'failed') && !hasOptions) {
    return (
      <div
        className={`flex w-full items-center justify-center bg-voto-surface text-sm text-voto-muted ${aspect} ${rounded ? 'rounded-2xl' : ''}`}
      >
        No image available
      </div>
    );
  }

  const containerProps = {
    className: `grid w-full overflow-hidden bg-voto-bg ${rounded ? 'gap-[3px] rounded-2xl' : 'gap-[2px]'}`,
    style: { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` },
    role: 'group',
    'aria-label': alt,
    'data-testid': 'poll-tiles',
  };

  return (
    <div {...containerProps}>
      {ordered.map((item, index) => {
        const caption = captions[index];
        const positionX = n > 1 ? `${(index / (n - 1)) * 100}%` : '0%';
        const ratio = tileRatio(n, layout, index);
        const span = layout === 'fill' && n === 3 && index === 2;
        const photo = { backgroundImage: `url(${src})`, backgroundSize: `${n * 100}% 100%`, backgroundPosition: `${positionX} 0%` };
        const tappable = votable && item?.id != null;
        const pct = percentOf(item?.votes_count, totalVotes);
        const chosen = revealed && item?.id != null && item.id === votedItemId;
        const isWinner = revealed && item?.id != null && item.id === winner;
        const names = revealed
          ? (votersByItem
              ? votersFor(votersByItem, item?.id).map((v) => (v.mine ? 'you' : v.name || 'someone'))
              : (item?.voters || []).map((v) => v.name || v.handle).filter(Boolean))
          : [];
        const shownNames = names.slice(0, 5);
        const extra = names.length - shownNames.length;

        const interact = tappable
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': `Vote ${caption || `option ${index + 1}`}`,
              onClick: () => onVote(item),
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onVote(item);
                }
              },
            }
          : {};

        return (
          <div
            key={index}
            {...interact}
            data-testid="poll-tile"
            className={[
              'relative overflow-hidden bg-voto-surface bg-no-repeat',
              ratio === 1 ? 'aspect-square' : '',
              tappable
                ? 'cursor-pointer transition hover:brightness-110 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-voto-coral'
                : '',
              voting ? 'pointer-events-none' : '',
            ].join(' ')}
            style={{
              ...(ratio !== 1 ? { aspectRatio: String(ratio) } : {}),
              ...(span ? { gridColumn: 'span 2' } : {}),
              ...(status === 'loaded' && ratio === 1 ? photo : {}),
            }}
          >
            {status === 'loaded' && ratio !== 1 && (
              <span
                aria-hidden="true"
                data-testid="tile-photo"
                className="pointer-events-none absolute bg-no-repeat"
                style={{ ...coverSquare(ratio), ...photo }}
              />
            )}
            {revealed && item?.id != null && (
              <span
                className={`absolute left-2 top-2 flex items-center gap-1.5 rounded-[10px] px-2 py-1 text-sm font-bold leading-none ${
                  isWinner ? 'bg-voto-green text-white' : 'bg-white/90 text-[#182230]'
                }`}
              >
                <span>{pct}%</span>
                {chosen && (
                  <i className={`text-[11px] font-medium not-italic ${isWinner ? 'text-white' : 'text-voto-coral'}`}>your vote</i>
                )}
              </span>
            )}

            {(shownNames.length > 0 || (showLabels && caption)) && (
              <div className="absolute inset-x-2 bottom-2.5 flex flex-col items-start gap-1">
                {shownNames.length > 0 && (
                  <div className="flex flex-wrap gap-1" aria-label={`Picked by ${names.join(', ')}`}>
                    {shownNames.map((name, i) => (
                      <span key={`${name}-${i}`} className="rounded-full bg-black/55 px-[7px] py-0.5 text-[11px] text-white">
                        {name}
                      </span>
                    ))}
                    {extra > 0 && (
                      <span className="rounded-full bg-black/55 px-[7px] py-0.5 text-[11px] text-white">+{extra}</span>
                    )}
                  </div>
                )}
                {showLabels && caption && (
                  <span
                    className="block max-w-full truncate text-xs font-semibold text-white"
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
                  >
                    {caption}
                  </span>
                )}
              </div>
            )}

            {revealed && item?.id != null && (
              <span className="absolute inset-x-0 bottom-0 h-[5px] bg-black/35" aria-hidden="true">
                <span className="absolute inset-y-0 left-0 bg-voto-coral" style={{ width: `${pct}%` }} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
